import { useState, useEffect } from "react";
import { Cookie } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const KEY = "rs-cookie-consent";

const readConsent = () => {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const CATEGORIES = [
  {
    id: "necessary",
    name: "Nødvendige cookies",
    desc: "Altid aktive. Bruges til teknisk nødvendige funktioner, sikkerhed og login.",
    locked: true,
  },
  {
    id: "stats",
    name: "Statistik",
    desc: "Valgfrit. Bruges til at forstå, hvordan hjemmesiden anvendes og forbedre brugeroplevelsen.",
  },
  {
    id: "marketing",
    name: "Marketing",
    desc: "Valgfrit. Bruges kun, hvis Royal Solutions anvender relevante marketing-/trackingværktøjer.",
  },
];

export const CookieConsent = () => {
  const [consent, setConsent] = useState(() => readConsent());
  const [bannerOpen, setBannerOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [prefs, setPrefs] = useState({ stats: false, marketing: false });

  useEffect(() => {
    const existing = readConsent();
    if (!existing) setBannerOpen(true);
    else setPrefs({ stats: !!existing.stats, marketing: !!existing.marketing });

    const openSettings = () => {
      const c = readConsent();
      setPrefs({ stats: !!c?.stats, marketing: !!c?.marketing });
      setSettingsOpen(true);
    };
    window.addEventListener("open-cookie-settings", openSettings);
    return () => window.removeEventListener("open-cookie-settings", openSettings);
  }, []);

  const persist = (data) => {
    const payload = { necessary: true, ...data, ts: new Date().toISOString() };
    localStorage.setItem(KEY, JSON.stringify(payload));
    setConsent(payload);
    setBannerOpen(false);
    setSettingsOpen(false);
  };

  const acceptAll = () => persist({ stats: true, marketing: true });
  const rejectNonEssential = () => persist({ stats: false, marketing: false });
  const saveCustom = () => persist({ stats: prefs.stats, marketing: prefs.marketing });

  return (
    <>
      {bannerOpen && (
        <div
          data-testid="cookie-banner"
          className="fixed inset-x-0 bottom-0 z-[60] border-t border-[#D4AF37]/30 bg-[#0A0A0A]/95 px-5 py-5 backdrop-blur-xl sm:px-8"
        >
          <div className="mx-auto flex max-w-5xl flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-start gap-3">
              <Cookie size={22} className="mt-0.5 shrink-0 text-[#D4AF37]" />
              <p className="text-sm leading-relaxed text-[#E7E3D8]">
                Vi bruger cookies for at få hjemmesiden til at fungere og, hvis du accepterer det,
                til statistik og markedsføring.
              </p>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap lg:shrink-0">
              <button
                type="button"
                data-testid="cookie-customize"
                onClick={() => setSettingsOpen(true)}
                className="rounded-full border border-[#D4AF37]/50 px-5 py-2.5 text-xs font-medium uppercase tracking-widest text-[#D4AF37] transition-colors duration-300 hover:bg-[#D4AF37]/10"
              >
                Tilpas
              </button>
              <button
                type="button"
                data-testid="cookie-reject"
                onClick={rejectNonEssential}
                className="rounded-full border border-white/20 px-5 py-2.5 text-xs font-medium uppercase tracking-widest text-[#F5F5F0] transition-colors duration-300 hover:border-white/50"
              >
                Afvis ikke-nødvendige
              </button>
              <button
                type="button"
                data-testid="cookie-accept-all"
                onClick={acceptAll}
                className="rounded-full bg-[#D4AF37] px-6 py-2.5 text-xs font-semibold uppercase tracking-widest text-[#0A0A0A] transition-colors duration-300 hover:bg-[#F0C74B]"
              >
                Accepter alle
              </button>
            </div>
          </div>
        </div>
      )}

      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent
          data-testid="cookie-settings-dialog"
          className="max-h-[88vh] overflow-y-auto border-[#D4AF37]/20 bg-[#141414] text-[#F5F5F0] sm:max-w-lg"
        >
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-[#F5F5F0]">Cookieindstillinger</DialogTitle>
            <DialogDescription className="text-xs text-gray-400">
              Vælg hvilke cookies du vil tillade. Du kan til enhver tid ændre eller trække dit
              samtykke tilbage her.
            </DialogDescription>
          </DialogHeader>

          <div className="mt-2 space-y-3">
            {CATEGORIES.map((c) => (
              <label
                key={c.id}
                className="flex items-start gap-3 rounded-lg border border-white/10 bg-[#0F0F0F] p-4"
              >
                <input
                  type="checkbox"
                  data-testid={`cookie-toggle-${c.id}`}
                  checked={c.locked ? true : prefs[c.id]}
                  disabled={c.locked}
                  onChange={(e) => !c.locked && setPrefs((p) => ({ ...p, [c.id]: e.target.checked }))}
                  className="mt-1 h-4 w-4 accent-[#D4AF37]"
                />
                <span>
                  <span className="text-sm font-semibold text-[#F5F5F0]">{c.name}</span>
                  <span className="mt-0.5 block text-xs leading-relaxed text-gray-400">{c.desc}</span>
                </span>
              </label>
            ))}
          </div>

          <div className="mt-5 flex flex-col gap-2 sm:flex-row">
            <button
              type="button"
              data-testid="cookie-settings-reject"
              onClick={rejectNonEssential}
              className="flex-1 rounded-full border border-white/20 px-4 py-2.5 text-xs font-medium uppercase tracking-widest text-[#F5F5F0] transition-colors duration-300 hover:border-white/50"
            >
              Afvis ikke-nødvendige
            </button>
            <button
              type="button"
              data-testid="cookie-settings-save"
              onClick={saveCustom}
              className="flex-1 rounded-full border border-[#D4AF37]/50 px-4 py-2.5 text-xs font-medium uppercase tracking-widest text-[#D4AF37] transition-colors duration-300 hover:bg-[#D4AF37]/10"
            >
              Gem valg
            </button>
            <button
              type="button"
              data-testid="cookie-settings-accept-all"
              onClick={acceptAll}
              className="flex-1 rounded-full bg-[#D4AF37] px-4 py-2.5 text-xs font-semibold uppercase tracking-widest text-[#0A0A0A] transition-colors duration-300 hover:bg-[#F0C74B]"
            >
              Accepter alle
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
